import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { AthleteClaimBanner } from "../components/ClaimBanners";
import { PhotoCropModal } from "../components/PhotoCropModal";
import { ProfileEditModal } from "../components/ProfileEditModal";
import { ScoutingDataPanel } from "../components/ScoutingDataPanel";
import { VideoShowcase } from "../components/VideoShowcase";
import { NSBVerifiedInline } from "../components/NSBVerifiedBadge";
import { PlayerHubSidebar } from "../components/PlayerHubSidebar";
import { ShareWithCoachModal } from "../components/ShareWithCoachModal";
import { ShareActivityPanel } from "../components/ShareActivityPanel";
import {
  BarChart3,
  Target,
  TrendingUp,
  DollarSign,
  User,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  BadgeCheck,
  Activity,
  ChevronRight,
  Bell,
  Calendar,
  MapPin,
  Star,
  GraduationCap,
  Clock,
  FileText,
  Users,
  Trophy,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
  ShieldCheck,
  ShieldAlert,
  School,
  Heart,
  Gift,
  Search,
  Plus,
  Trash2,
  Camera
} from "lucide-react";
import { Input } from "../components/ui/input";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Calculate profile completeness
const calculateProfileCompleteness = (athlete) => {
  if (!athlete) return { percentage: 0, missing: [], completed: [] };
  
  const fields = [
    { key: 'name', label: 'Full Name', weight: 10 },
    { key: 'position_primary', label: 'Primary Position', weight: 10 },
    { key: 'height', label: 'Height', weight: 5 },
    { key: 'weight', label: 'Weight', weight: 5 },
    { key: 'school', label: 'School', weight: 10 },
    { key: 'graduation_year', label: 'Graduation Year', weight: 10 },
    { key: 'location', label: 'Location', weight: 5 },
    { key: 'bio', label: 'Bio', weight: 10 },
    { key: 'highlight_video_url', label: 'Highlight Video', weight: 15 },
    { key: 'gpa', label: 'GPA', weight: 5 },
    { key: 'profile_image', label: 'Profile Photo', weight: 10 },
    { key: 'social_links.twitter', label: 'Twitter/X', weight: 2.5 },
    { key: 'social_links.instagram', label: 'Instagram', weight: 2.5 },
  ];
  
  let totalWeight = 0;
  let completedWeight = 0;
  const missing = [];
  const completed = [];
  
  fields.forEach(field => {
    totalWeight += field.weight;
    const value = field.key.includes('.') 
      ? field.key.split('.').reduce((obj, key) => obj?.[key], athlete)
      : athlete[field.key];
    
    if (value && value !== '' && value !== '-') {
      completedWeight += field.weight;
      completed.push(field.label);
    } else {
      missing.push(field.label);
    }
  });
  
  return {
    percentage: Math.round((completedWeight / totalWeight) * 100),
    missing,
    completed
  };
};

// Profile Completeness Component
const ProfileCompleteness = ({ athlete, onEditProfile }) => {
  const { percentage, missing } = calculateProfileCompleteness(athlete);
  
  const getColor = () => {
    if (percentage >= 80) return { bg: 'bg-green-500', text: 'text-green-600', light: 'bg-green-50' };
    if (percentage >= 50) return { bg: 'bg-yellow-500', text: 'text-yellow-600', light: 'bg-yellow-50' };
    return { bg: 'bg-red-500', text: 'text-red-600', light: 'bg-red-50' };
  };
  
  const colors = getColor();
  
  return (
    <div className={`${colors.light} rounded-xl p-4 mb-6 border border-${colors.text.replace('text-', '')}/20`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
          {percentage >= 80 ? (
            <CheckCircle size={18} className="text-green-500" />
          ) : (
            <AlertCircle size={18} className={colors.text} />
          )}
          Profile Completeness
        </h4>
        <span className={`text-lg font-bold ${colors.text}`}>{percentage}%</span>
      </div>
      
      {/* Progress Bar */}
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
        <div 
          className={`h-full ${colors.bg} rounded-full transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      {percentage < 100 && missing.length > 0 && (
        <div className="text-sm">
          <p className="text-gray-600 mb-2">
            {percentage >= 80 ? 'Almost there! Add:' : percentage >= 50 ? 'Add more to stand out:' : 'Complete your profile:'}
          </p>
          <div className="flex flex-wrap gap-1 mb-3">
            {missing.slice(0, 4).map((item, i) => (
              <span key={i} className="px-2 py-0.5 bg-white rounded text-xs text-gray-600 border">
                {item}
              </span>
            ))}
            {missing.length > 4 && (
              <span className="px-2 py-0.5 text-xs text-gray-500">
                +{missing.length - 4} more
              </span>
            )}
          </div>
          <button 
            onClick={onEditProfile}
            className={`text-sm font-medium ${colors.text} hover:underline`}
          >
            Complete Profile →
          </button>
        </div>
      )}
      
      {percentage === 100 && (
        <p className="text-sm text-green-600">
          ✨ Your profile is complete! Coaches can see all your info.
        </p>
      )}
    </div>
  );
};

// NSB Logo Component
import { NSBLogo } from "../components/NSBLogo";

export default function AthleteDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [athlete, setAthlete] = useState(null);
  const [evaluations, setEvaluations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Combines state
  const [publicCombines, setPublicCombines] = useState([]);
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [combinesLoading, setCombinesLoading] = useState(false);
  const [registering, setRegistering] = useState(null);

  // Mock data for demo (will be replaced with real API data)
  const [playerProfile, setPlayerProfile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [cropModal, setCropModal] = useState(null); // { src, type: 'profile'|'action' }
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showShareCoach, setShowShareCoach] = useState(false);
  const profilePhotoRef = useRef(null);
  const actionPhotoRef = useRef(null);

  const handlePhotoSelect = (file, photoType) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropModal({ src: reader.result, type: photoType });
    };
    reader.readAsDataURL(file);
  };

  const handleCroppedUpload = async (blob) => {
    const photoType = cropModal.type;
    setUploadingPhoto(photoType);
    setCropModal(null);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", blob, `${photoType}_photo.jpg`);
      const res = await fetch(`${API_URL}/api/athlete/upload-photo?photo_type=${photoType}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${photoType === "profile" ? "Profile" : "Action"} photo updated!`);
        const field = photoType === "profile" ? "profile_image" : "action_image";
        setPlayerProfile(prev => prev ? { ...prev, [field]: data.url } : prev);
        setAthlete(prev => prev ? { ...prev, [field]: data.url } : prev);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Upload failed");
      }
    } catch {
      toast.error("Photo upload failed");
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleProfileSave = (result) => {
    // Refresh the dashboard data
    fetchData();
  };

  const handleVisibilityChange = async (field, value) => {
    const newVis = { ...(playerProfile?.field_visibility || {}), [field]: value };
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/api/athlete/update-profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ field_visibility: newVis }),
      });
      setPlayerProfile(prev => prev ? { ...prev, field_visibility: newVis } : prev);
      const labels = { public: "Everyone", coaches: "Coaches & Scouts", private: "Only You" };
      toast.success(`${field.replace(/_/g, " ")} now visible to: ${labels[value]}`);
    } catch {
      toast.error("Failed to update visibility");
    }
  };

  const [recentActivity, setRecentActivity] = useState([]);

  const mockUpcoming = [
    { id: 1, title: "Regional Showcase - Dallas", date: "March 15, 2024", type: "showcase" }
  ];

  // School interests and tuition rewards state
  const [schoolInterests, setSchoolInterests] = useState([]);
  const [tuitionRewards, setTuitionRewards] = useState(null);
  const [allSchools, setAllSchools] = useState([]);
  const [schoolSearchQuery, setSchoolSearchQuery] = useState("");
  const [schoolsLoading, setSchoolsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch schools and tuition when tab changes
  useEffect(() => {
    if (activeTab === "combines") {
      fetchCombines();
    } else if (activeTab === "schools") {
      fetchSchoolInterests();
      fetchAllSchools();
    } else if (activeTab === "tuition") {
      fetchTuitionRewards();
    }
  }, [activeTab]);

  const fetchSchoolInterests = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/athlete/school-interests`, {
        credentials: "include",
        headers,
      });
      if (res.ok) {
        const data = await res.json();
        setSchoolInterests(data.interests || []);
      }
    } catch (error) {
      console.error("Error fetching school interests:", error);
    }
  };

  const fetchAllSchools = async () => {
    setSchoolsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/coaches?limit=2000`, {
        credentials: "include",
        headers,
      });
      if (res.ok) {
        const data = await res.json();
        // Get unique schools
        const uniqueSchools = [...new Set(data.coaches?.map(c => c.school).filter(Boolean))];
        setAllSchools(uniqueSchools.sort());
      }
    } catch (error) {
      console.error("Error fetching schools:", error);
    } finally {
      setSchoolsLoading(false);
    }
  };

  const fetchTuitionRewards = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/athlete/tuition-rewards`, {
        credentials: "include",
        headers,
      });
      if (res.ok) {
        const data = await res.json();
        setTuitionRewards(data.tuition_rewards);
      }
    } catch (error) {
      console.error("Error fetching tuition rewards:", error);
    }
  };

  const handleAddSchoolInterest = async (schoolName) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/athlete/school-interests`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ school_id: schoolName }),
      });
      if (res.ok) {
        toast.success(`Added ${schoolName} to your Top 20 list!`);
        fetchSchoolInterests();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add school");
      }
    } catch (error) {
      toast.error("Error adding school interest");
    }
  };

  const handleRemoveSchoolInterest = async (interestId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/athlete/school-interests/${interestId}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        toast.success("School removed from your list");
        fetchSchoolInterests();
      }
    } catch (error) {
      toast.error("Error removing school");
    }
  };

  const fetchCombines = async () => {
    setCombinesLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch public combines
      const combinesRes = await fetch(`${API_URL}/api/combines/public?upcoming_only=false`, {
        credentials: "include",
        headers,
      });
      
      if (combinesRes.ok) {
        const data = await combinesRes.json();
        setPublicCombines(data.events || []);
      }
      
      // Fetch my registrations
      const regsRes = await fetch(`${API_URL}/api/athlete/my-registrations`, {
        credentials: "include",
        headers,
      });
      
      if (regsRes.ok) {
        const data = await regsRes.json();
        setMyRegistrations(data.registrations || []);
      }
    } catch (error) {
      console.error("Error fetching combines:", error);
    } finally {
      setCombinesLoading(false);
    }
  };

  const handleRegisterForCombine = async (eventId) => {
    setRegistering(eventId);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/combines/${eventId}/self-register`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      
      if (response.ok) {
        toast.success("Successfully registered for combine!");
        fetchCombines();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to register");
      }
    } catch (error) {
      toast.error("Failed to register for combine");
    } finally {
      setRegistering(null);
    }
  };

  const handleCancelRegistration = async (registrationId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/athlete/registrations/${registrationId}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      
      if (response.ok) {
        toast.success("Registration cancelled");
        fetchCombines();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to cancel registration");
      }
    } catch (error) {
      toast.error("Failed to cancel registration");
    }
  };

  const isRegistered = (eventId) => {
    return myRegistrations.some(reg => reg.event_id === eventId);
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Get user info
      const userRes = await fetch(`${API_URL}/api/auth/me`, {
        credentials: "include",
        headers,
      });

      if (!userRes.ok) {
        navigate("/login");
        return;
      }

      const userData = await userRes.json();
      setUser(userData);

      // Get merged dashboard profile (admin + athlete data)
      const profileRes = await fetch(`${API_URL}/api/athlete/my-dashboard-profile`, {
        credentials: "include",
        headers,
      });

      if (profileRes.ok) {
        const profileData = await profileRes.json();
        if (profileData.profile) {
          setPlayerProfile(profileData.profile);
          setAthlete(profileData.profile);
          // Get evaluations
          const evalsRes = await fetch(`${API_URL}/api/evaluations/${profileData.profile.athlete_id}/summary`, {
            credentials: "include",
            headers,
          });
          if (evalsRes.ok) {
            setEvaluations(await evalsRes.json());
          }
        }
      }

      // Fallback: if no player profile, try athletes collection 
      if (!playerProfile) {
        const athletesRes = await fetch(`${API_URL}/api/athletes`, {
          credentials: "include",
          headers,
        });
        if (athletesRes.ok) {
          const athletes = await athletesRes.json();
          const userAthlete = athletes.find(a => a.user_id === userData.user_id);
          if (userAthlete) {
            setAthlete(userAthlete);
            const evalsRes = await fetch(`${API_URL}/api/evaluations/${userAthlete.athlete_id}/summary`, {
              credentials: "include",
              headers,
            });
            if (evalsRes.ok) {
              setEvaluations(await evalsRes.json());
            }
          }
        }
      }

      // Fetch recent activity
      const actRes = await fetch(`${API_URL}/api/athlete/my-activity`, {
        credentials: "include",
        headers,
      });
      if (actRes.ok) {
        const actData = await actRes.json();
        setRecentActivity(actData.activities || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      // Continue with logout even if API fails
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Use real data from merged profile, with sensible fallbacks
  const displayName = playerProfile?.name || athlete?.name || user?.name || "Athlete";
  const displayPosition = playerProfile?.position_primary || athlete?.positions?.join(", ") || "--";
  const displaySchool = playerProfile?.school || athlete?.school || "--";
  const displayLocation = playerProfile?.location || athlete?.location || "--";
  const displayLevel = playerProfile?.graduation_year ? `Class of ${playerProfile.graduation_year}` : (playerProfile?.level || athlete?.level || "--");
  
  // Real metrics from admin-managed player profile
  const metrics = playerProfile?.metrics || {};
  const isVerifiedMetric = (key) => !!(metrics[key]);
  const performanceMetrics = {
    exitVelocity: { value: metrics.exit_velocity ? `${metrics.exit_velocity} mph` : (metrics.fastball_velocity ? `${metrics.fastball_velocity} mph` : "--"), label: metrics.exit_velocity ? "Exit Velocity" : "Max Velo", verified: isVerifiedMetric("exit_velocity") || isVerifiedMetric("fastball_velocity") },
    avgFBVelo: { value: metrics.avg_fastball_velocity ? `${metrics.avg_fastball_velocity} mph` : "--", label: "Avg FB Velo", verified: isVerifiedMetric("avg_fastball_velocity") },
    sixtyYardDash: { value: metrics.sixty_time ? `${metrics.sixty_time}s` : "--", label: "60-Yard Dash", verified: isVerifiedMetric("sixty_time") },
    spinRate: { value: metrics.spin_rate ? `${metrics.spin_rate} rpm` : "--", label: "Max Spin", verified: isVerifiedMetric("spin_rate") },
    extension: { value: metrics.extension ? `${metrics.extension} ft` : "--", label: "Extension", verified: isVerifiedMetric("extension") },
    popTime: { value: metrics.pop_time ? `${metrics.pop_time}s` : "--", label: "Pop Time", verified: isVerifiedMetric("pop_time") },
  };
  // Filter out empty metrics — show all verified ones
  const activeMetrics = Object.entries(performanceMetrics).filter(([_, m]) => m.value !== "--");
  if (activeMetrics.length === 0) {
    // Show placeholder metrics
    activeMetrics.push(["noData", { value: "--", label: "No Verified Metrics Yet" }]);
  }
  
  // Calculate NSB Score from evaluations or tool grades (20-80 scale)
  const getNSBScore = () => {
    // Check tool_grades from admin-extracted scouting report
    if (playerProfile?.tool_grades?.overall?.current) {
      return { score: playerProfile.tool_grades.overall.current, source: "Scouting Report" };
    }
    if (evaluations?.ppi?.current_composite) {
      return { score: evaluations.ppi.current_composite, source: "PPI" };
    }
    if (evaluations?.api?.score) {
      return { score: evaluations.api.score, source: "API" };
    }
    // No verified score yet
    return null;
  };
  
  const nsbScore = getNSBScore();
  
  const getScoreLabel = (score) => {
    if (!score) return { label: "Unrated", color: "text-gray-400" };
    if (score >= 80) return { label: "Elite", color: "text-blue-900" };
    if (score >= 70) return { label: "Plus-Plus", color: "text-green-600" };
    if (score >= 60) return { label: "Plus", color: "text-blue-600" };
    if (score >= 55) return { label: "Above Avg", color: "text-cyan-600" };
    if (score >= 50) return { label: "Average", color: "text-gray-600" };
    if (score >= 45) return { label: "Below Avg", color: "text-yellow-600" };
    return { label: "Developing", color: "text-orange-600" };
  };
  
  const scoreInfo = getScoreLabel(nsbScore?.score);

  return (
    <div className="min-h-screen bg-gray-100" data-testid="athlete-dashboard">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <NSBLogo className="h-10" light={false} />
            </Link>

            <div className="flex items-center gap-4">
              <button className="relative p-2 text-gray-400 hover:text-gray-600">
                <Bell size={20} />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <User size={16} className="text-gray-500" />
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-600"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut size={16} className="mr-2" />
                  Sign Out
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content + Player Hub Sidebar */}
      <div className="flex gap-0 max-w-[100rem] mx-auto">
        <PlayerHubSidebar activeTab={activeTab} onSelectTab={setActiveTab} />
        <div className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Player Dashboard</h1>
          <p className="text-gray-500 mt-1">
            Welcome back, <span className="font-medium text-gray-700">{displayName}</span>
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
              <Star size={14} /> NSB Universe Member
            </span>
          </div>
        </div>

        {/* Claim Scouting Report Banner */}
        <AthleteClaimBanner />

        {/* Dashboard Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white border border-gray-200 rounded-lg p-1 mb-6 flex-wrap">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white rounded-md px-4 py-2">
              Overview
            </TabsTrigger>
            <TabsTrigger value="performance" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white rounded-md px-4 py-2">
              Performance
            </TabsTrigger>
            <TabsTrigger value="combines" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white rounded-md px-4 py-2">
              Combines
            </TabsTrigger>
            <TabsTrigger value="schools" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white rounded-md px-4 py-2">
              My Schools
            </TabsTrigger>
            <TabsTrigger value="tuition" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white rounded-md px-4 py-2">
              Tuition Rewards
            </TabsTrigger>
            <TabsTrigger value="videos" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white rounded-md px-4 py-2" data-testid="video-tab">
              Video
            </TabsTrigger>
            <TabsTrigger value="recruiting" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white rounded-md px-4 py-2">
              Recruiting
            </TabsTrigger>
            <TabsTrigger value="development" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white rounded-md px-4 py-2">
              Development
            </TabsTrigger>
            <TabsTrigger value="shares" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white rounded-md px-4 py-2" data-testid="share-activity-tab">
              Share Activity
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-0">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Left Column - Player Card */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm p-6" data-testid="player-card">
                  {/* Player Photo & Basic Info */}
                  <div className="text-center mb-6">
                    <div 
                      className="w-24 h-24 rounded-full bg-gray-200 mx-auto mb-4 overflow-hidden relative group cursor-pointer"
                      onClick={() => profilePhotoRef.current?.click()}
                      data-testid="profile-photo-upload"
                    >
                      {uploadingPhoto === "profile" ? (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : athlete?.profile_image ? (
                        <img src={athlete.profile_image.startsWith('/') ? `${API_URL}${athlete.profile_image}` : athlete.profile_image} alt={displayName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User size={40} className="text-gray-400" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                        <Camera size={20} className="text-white" />
                      </div>
                      <input
                        ref={profilePhotoRef}
                        type="file"
                        className="hidden"
                        accept=".jpg,.jpeg,.png,.gif,.webp"
                        onChange={(e) => handlePhotoSelect(e.target.files[0], "profile")}
                      />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 flex items-center justify-center gap-2">
                      {displayName}
                      {athlete?.is_verified && <BadgeCheck size={18} className="text-blue-500" />}
                    </h3>
                    <p className="text-gray-500">{displayPosition} • {displayLevel}</p>
                  </div>

                  {/* School & Location */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-gray-600">
                      <GraduationCap size={18} className="text-gray-400" />
                      <span className="text-sm">{displaySchool}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <MapPin size={18} className="text-gray-400" />
                      <span className="text-sm">{displayLocation}</span>
                    </div>
                  </div>

                  {/* Physical Info & NSB Verified */}
                  {(playerProfile?.height || playerProfile?.weight || playerProfile?.bats || playerProfile?.throws) && (
                    <div className="mb-4 space-y-1.5">
                      <div className="flex flex-wrap gap-2 text-xs">
                        {playerProfile?.height && <span className="bg-gray-100 px-2 py-1 rounded">{playerProfile.height}</span>}
                        {playerProfile?.weight && <span className="bg-gray-100 px-2 py-1 rounded">{playerProfile.weight} lbs</span>}
                        {(playerProfile?.bats || playerProfile?.throws) && (
                          <span className="bg-gray-100 px-2 py-1 rounded">
                            {playerProfile.bats && `B: ${playerProfile.bats}`}{playerProfile.bats && playerProfile.throws && " / "}{playerProfile.throws && `T: ${playerProfile.throws}`}
                          </span>
                        )}
                      </div>
                      {playerProfile?.has_scouting_report && (
                        <div className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-md w-fit">
                          <ShieldCheck size={12} />
                          <span className="font-medium">NSB Verified Athlete</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Edit Profile Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mb-2"
                    onClick={() => setShowEditProfile(true)}
                    data-testid="edit-profile-btn"
                  >
                    <Settings size={14} className="mr-1.5" /> Edit Profile
                  </Button>

                  {/* Share with Coach — generates a token-gated private link */}
                  <Button
                    size="sm"
                    className="w-full mb-4 bg-gradient-to-r from-blue-900 to-blue-700 text-white hover:from-blue-800 hover:to-blue-600 shadow-sm"
                    onClick={() => setShowShareCoach(true)}
                    data-testid="share-with-coach-btn"
                  >
                    <Users size={14} className="mr-1.5" /> Share with a Coach
                  </Button>

                  {/* NSB Score - 20-80 Scale */}
                  <div className="bg-gradient-to-r from-blue-800 to-blue-900 rounded-xl p-4 text-white text-center">
                    <div className="text-sm opacity-80 mb-1">NSB Score (20-80)</div>
                    {nsbScore ? (
                      <>
                        <div className="text-4xl font-bold">{nsbScore.score}</div>
                        <div className="text-xs mt-1 opacity-70">{scoreInfo.label}</div>
                      </>
                    ) : (
                      <>
                        <div className="text-2xl font-bold">--</div>
                        <div className="text-xs mt-1 opacity-70">Not Yet Evaluated</div>
                      </>
                    )}
                  </div>
                </div>

                {/* Action Photo Upload */}
                <div className="bg-white rounded-xl shadow-sm p-4 mt-4" data-testid="action-photo-section">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Camera size={16} className="text-blue-500" />
                    Action Photo
                  </h4>
                  <div 
                    className="relative rounded-lg overflow-hidden border-2 border-dashed border-gray-200 hover:border-blue-400 cursor-pointer transition-colors group"
                    onClick={() => actionPhotoRef.current?.click()}
                    data-testid="action-photo-upload"
                  >
                    {uploadingPhoto === "action" ? (
                      <div className="aspect-video flex items-center justify-center bg-gray-50">
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : (playerProfile?.action_image || athlete?.action_image) ? (
                      <div className="relative aspect-video">
                        <img 
                          src={(() => { const img = playerProfile?.action_image || athlete?.action_image; return img?.startsWith('/') ? `${API_URL}${img}` : img; })()}
                          alt="Action"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Camera size={24} className="text-white" />
                          <span className="text-white text-sm ml-2">Change Photo</span>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-video flex flex-col items-center justify-center bg-gray-50 text-gray-400">
                        <Camera size={28} />
                        <span className="text-xs mt-2">Upload Action Photo</span>
                      </div>
                    )}
                    <input
                      ref={actionPhotoRef}
                      type="file"
                      className="hidden"
                      accept=".jpg,.jpeg,.png,.gif,.webp"
                      onChange={(e) => handlePhotoSelect(e.target.files[0], "action")}
                    />
                  </div>
                </div>

                {/* Profile Completeness */}
                <ProfileCompleteness 
                  athlete={athlete} 
                  onEditProfile={() => navigate("/create-profile")}
                />

                {/* Verified vs Self-Reported Metrics */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <ShieldCheck size={18} className="text-blue-500" />
                    Metrics Verification Status
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {/* Verified Metrics */}
                    <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                      <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck size={14} className="text-green-600" />
                        <span className="text-xs font-semibold text-green-700 uppercase">NSB Verified</span>
                      </div>
                      <p className="text-xs text-green-600">
                        Measured by NSB scouts or at official combines
                      </p>
                      <div className="mt-2 text-lg font-bold text-green-700">
                        {Object.values(playerProfile?.metrics || {}).filter(v => v !== null && v !== undefined).length || 0} metrics
                      </div>
                    </div>
                    
                    {/* Self-Reported Metrics */}
                    <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                      <div className="flex items-center gap-2 mb-2">
                        <ShieldAlert size={14} className="text-yellow-600" />
                        <span className="text-xs font-semibold text-yellow-700 uppercase">Self-Reported</span>
                      </div>
                      <p className="text-xs text-yellow-600">
                        Entered by you or your parent/guardian
                      </p>
                      <div className="mt-2 text-lg font-bold text-yellow-700">
                        {athlete?.self_reported_metrics && Object.values(athlete.self_reported_metrics).filter(Boolean).length || 0} metrics
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-3">
                    💡 Attend an NSB combine to get your metrics verified and improve visibility to coaches.
                  </p>
                </div>
              </div>

              {/* Right Column - Performance & Activity */}
              <div className="lg:col-span-2 space-y-6">
                {/* Performance Metrics */}
                <div className="bg-white rounded-xl shadow-sm p-6" data-testid="performance-metrics">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Performance Metrics</h3>
                    {playerProfile?.has_scouting_report && (
                      <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
                        <ShieldCheck size={12} /> NSB Verified
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {activeMetrics.map(([key, metric]) => (
                      <div key={key} className="bg-gray-50 rounded-lg p-4 relative">
                        <div className="text-sm text-gray-500 mb-1 flex items-center gap-1.5">
                          {metric.label}
                          {metric.verified && metric.value !== "--" && (
                            <NSBVerifiedInline verified={true} />
                          )}
                        </div>
                        <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
                      </div>
                    ))}
                  </div>
                  {activeMetrics.length <= 1 && activeMetrics[0]?.[0] === "noData" && (
                    <p className="text-sm text-gray-500 mt-3 text-center">
                      Attend an NSB combine or get evaluated to see your verified metrics here.
                    </p>
                  )}
                </div>

                {/* Full Scouting Data & Files Panel */}
                <ScoutingDataPanel
                  profile={playerProfile}
                  onVisibilityChange={handleVisibilityChange}
                />

                {/* Recent Activity */}
                <div className="bg-white rounded-xl shadow-sm p-6" data-testid="recent-activity">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Activity</h3>
                  <div className="space-y-3">
                    {recentActivity.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-4">No recent activity yet.</p>
                    ) : recentActivity.map((activity, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                          activity.type === 'file' ? 'bg-blue-100 text-blue-600' :
                          activity.type === 'evaluation' ? 'bg-green-100 text-green-600' :
                          'bg-purple-100 text-purple-600'
                        }`}>
                          {activity.type === 'file' && <FileText size={16} />}
                          {activity.type === 'evaluation' && <Activity size={16} />}
                          {activity.type === 'profile' && <User size={16} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{activity.title}</p>
                          <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <Clock size={10} />
                            {activity.date ? new Date(activity.date).toLocaleDateString() : "--"}
                            {activity.detail && <span className="ml-1 text-gray-400">{activity.detail}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Upcoming Events */}
                <div className="bg-white rounded-xl shadow-sm p-6" data-testid="upcoming-events">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Upcoming</h3>
                  {mockUpcoming.map((event) => (
                    <div key={event.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Calendar size={24} className="text-blue-900" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{event.title}</div>
                          <div className="text-sm text-gray-500">{event.date}</div>
                        </div>
                      </div>
                      <Button variant="outline" className="text-blue-900 border-purple-200 hover:bg-purple-50">
                        View Details
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="videos" data-testid="video-tab-content">
            <VideoShowcase
              athleteId={athlete?.athlete_id}
              canEdit={true}
              isAdmin={false}
            />
          </TabsContent>

          <TabsContent value="performance">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">The Index Stack™</h3>
              <p className="text-gray-500 mb-6">Your comprehensive evaluation across all performance metrics</p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white">
                  <div className="text-sm opacity-80 mb-2">API</div>
                  <div className="text-3xl font-bold mb-2">{evaluations?.api?.score || "--"}</div>
                  <div className="text-xs opacity-80">Athlete Performance Index</div>
                  <div className="mt-4 text-xs">
                    Raw athletic capacity: speed, strength, power
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                  <div className="text-sm opacity-80 mb-2">SPI</div>
                  <div className="text-3xl font-bold mb-2">{evaluations?.spi?.score || "--"}</div>
                  <div className="text-xs opacity-80">Skills Performance Index</div>
                  <div className="mt-4 text-xs">
                    Tool execution using MLB 20-80 scale
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
                  <div className="text-sm opacity-80 mb-2">PPI</div>
                  <div className="text-3xl font-bold mb-2">{evaluations?.ppi?.score || "--"}</div>
                  <div className="text-xs opacity-80">Player Performance Index</div>
                  <div className="mt-4 text-xs">
                    On-field production metrics
                  </div>
                </div>

                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-6 text-white">
                  <div className="text-sm opacity-80 mb-2">PVI</div>
                  <div className="text-3xl font-bold mb-2">{evaluations?.pvi?.score || "--"}</div>
                  <div className="text-xs opacity-80">Player Value Index</div>
                  <div className="mt-4 text-xs">
                    Market value and opportunity
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 italic text-center">
                  "PPI tells us how good the player is. <span className="text-blue-900 font-semibold">PVI tells us where they belong — and what they're worth.</span>"
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="combines">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* My Registrations */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <CheckCircle size={18} className="text-green-500" />
                    My Registrations
                  </h3>
                  
                  {combinesLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-800 border-t-transparent mx-auto" />
                    </div>
                  ) : myRegistrations.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                      <p>No registrations yet</p>
                      <p className="text-sm text-gray-400">Browse available combines to register</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {myRegistrations.map((reg) => (
                        <div key={reg.registration_id} className="border border-green-100 bg-green-50 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900">{reg.event?.name || "Event"}</h4>
                              <p className="text-sm text-gray-500">{reg.event?.location}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {reg.event?.start_date ? new Date(reg.event.start_date).toLocaleDateString() : "TBD"}
                              </p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              reg.status === "registered" ? "bg-green-100 text-green-700" :
                              reg.status === "attended" ? "bg-blue-100 text-blue-700" :
                              "bg-gray-100 text-gray-600"
                            }`}>
                              {reg.status}
                            </span>
                          </div>
                          {reg.status === "registered" && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="w-full mt-3 text-red-600 hover:bg-red-50"
                              onClick={() => handleCancelRegistration(reg.registration_id)}
                            >
                              <XCircle size={14} className="mr-2" />
                              Cancel Registration
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Available Combines */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Trophy size={18} className="text-blue-800" />
                    Available Combines & Events
                  </h3>
                  
                  {combinesLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-800 border-t-transparent mx-auto" />
                      <p className="text-gray-500 mt-4">Loading events...</p>
                    </div>
                  ) : publicCombines.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">No upcoming combines available</p>
                      <p className="text-sm text-gray-400">Check back soon for new events!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {publicCombines.map((event) => {
                        const registered = isRegistered(event.event_id);
                        const eventDate = event.start_date ? new Date(event.start_date) : null;
                        const isPast = eventDate && eventDate < new Date();
                        
                        return (
                          <div 
                            key={event.event_id} 
                            className={`border rounded-xl p-5 transition-all ${
                              registered ? 'border-green-200 bg-green-50' : 'border-gray-200 hover:border-purple-200 hover:shadow-md'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-gray-900 text-lg">{event.name}</h4>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    event.event_type === "combine" ? "bg-purple-100 text-purple-700" :
                                    event.event_type === "showcase" ? "bg-blue-100 text-blue-700" :
                                    event.event_type === "camp" ? "bg-green-100 text-green-700" :
                                    "bg-gray-100 text-gray-600"
                                  }`}>
                                    {event.event_type}
                                  </span>
                                  {registered && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium flex items-center gap-1">
                                      <CheckCircle size={12} /> Registered
                                    </span>
                                  )}
                                </div>
                                
                                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                                  <span className="flex items-center gap-1">
                                    <MapPin size={14} /> {event.location || "TBD"}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar size={14} /> {eventDate ? eventDate.toLocaleDateString() : "TBD"}
                                  </span>
                                  {event.registration_fee && (
                                    <span className="font-medium text-gray-800">
                                      ${event.registration_fee}
                                    </span>
                                  )}
                                </div>
                                
                                {event.description && (
                                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">{event.description}</p>
                                )}
                                
                                {/* Metrics tested */}
                                {event.metrics_tested && event.metrics_tested.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {event.metrics_tested.map((metric, i) => (
                                      <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                        {metric.replace(/_/g, ' ')}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex-shrink-0">
                                {!registered && !isPast ? (
                                  <Button
                                    onClick={() => handleRegisterForCombine(event.event_id)}
                                    disabled={registering === event.event_id}
                                    className="bg-blue-900 hover:bg-purple-700 text-white"
                                  >
                                    {registering === event.event_id ? (
                                      <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                                        Registering...
                                      </>
                                    ) : (
                                      <>Register Now</>
                                    )}
                                  </Button>
                                ) : isPast ? (
                                  <span className="px-3 py-2 bg-gray-100 text-gray-500 rounded-lg text-sm">
                                    Event Passed
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* My Schools (Top 20 Interest List) - Private */}
          <TabsContent value="schools">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Current Interest List */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Heart size={18} className="text-red-500" />
                    My Top 20 Schools
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Schools will be notified of your interest. This list is private and not shown on your public profile.
                  </p>
                  
                  {schoolInterests.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <School className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                      <p>No schools selected yet</p>
                      <p className="text-sm text-gray-400">Search and add schools you're interested in</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {schoolInterests.map((interest, idx) => (
                        <div key={interest.interest_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <div className="font-medium text-sm text-gray-900">{idx + 1}. {interest.school_name}</div>
                            <div className="text-xs text-gray-500">{interest.division}</div>
                          </div>
                          <button 
                            onClick={() => handleRemoveSchoolInterest(interest.interest_id)}
                            className="p-1 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs text-gray-400">
                      {schoolInterests.length} / 20 schools selected
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Search Schools */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Search size={18} className="text-blue-500" />
                    Find Schools
                  </h3>
                  
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                      placeholder="Search schools by name..."
                      value={schoolSearchQuery}
                      onChange={(e) => setSchoolSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {schoolsLoading ? (
                      <div className="text-center py-8 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                        Loading schools...
                      </div>
                    ) : allSchools.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <School size={32} className="mx-auto mb-2 text-gray-300" />
                        <p>No schools loaded yet.</p>
                        <p className="text-sm">Try refreshing the page.</p>
                      </div>
                    ) : (
                      allSchools
                        .filter(s => s.toLowerCase().includes(schoolSearchQuery.toLowerCase()))
                        .slice(0, 50)
                        .map((school) => {
                          const isSelected = schoolInterests.some(i => i.school_name === school);
                          return (
                            <div 
                              key={school} 
                              className={`flex items-center justify-between p-3 rounded-lg border ${isSelected ? 'bg-green-50 border-green-200' : 'hover:bg-gray-50'}`}
                            >
                              <span className="text-sm text-gray-900">{school}</span>
                              {isSelected ? (
                                <span className="text-xs text-green-600 flex items-center gap-1">
                                  <CheckCircle size={14} /> Added
                                </span>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleAddSchoolInterest(school)}
                                  disabled={schoolInterests.length >= 20}
                                >
                                  <Plus size={14} className="mr-1" /> Add
                                </Button>
                              )}
                            </div>
                          );
                        })
                    )}
                    {!schoolsLoading && allSchools.length > 0 && schoolSearchQuery && allSchools.filter(s => s.toLowerCase().includes(schoolSearchQuery.toLowerCase())).length === 0 && (
                      <div className="text-center py-4 text-gray-500">
                        No schools found matching &ldquo;{schoolSearchQuery}&rdquo;
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* NSB Tuition Rewards - Private */}
          <TabsContent value="tuition">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Rewards Balance */}
              <div className="lg:col-span-1">
                <div className="bg-gradient-to-br from-blue-900 to-blue-600 rounded-xl p-6 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Gift size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold">NSB Tuition Rewards</h3>
                      <p className="text-xs opacity-80">Your scholarship credits</p>
                    </div>
                  </div>
                  
                  <div className="text-center py-6">
                    <div className="text-5xl font-black">
                      {tuitionRewards?.total_points?.toLocaleString() || 0}
                    </div>
                    <div className="text-sm opacity-80 mt-1">Total Points</div>
                  </div>
                  
                  <div className="bg-white/10 rounded-lg p-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="opacity-80">Estimated Value</span>
                      <span className="font-bold">${tuitionRewards?.total_points?.toLocaleString() || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="opacity-80">Monthly Earning Rate</span>
                      <span className="font-bold">+{tuitionRewards?.points_monthly_rate || 100} pts</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Your Membership</h4>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="font-medium text-purple-700">{tuitionRewards?.tier_info?.name || "NSB Universe Freemium"}</div>
                    <div className="text-sm text-blue-900">{tuitionRewards?.tier_info?.tuition_points_annual?.toLocaleString() || 1200} pts/year</div>
                  </div>
                  <Button className="w-full mt-4 bg-blue-900 hover:bg-purple-700">
                    Upgrade Membership
                  </Button>
                </div>
              </div>
              
              {/* Rewards Info */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">How NSB Tuition Rewards Work</h3>
                  
                  <div className="space-y-4">
                    <div className="flex gap-4 p-4 bg-blue-50 rounded-lg">
                      <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">1</div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Earn Points Monthly</h4>
                        <p className="text-sm text-gray-600">Accumulate points based on your membership tier. Higher tiers earn more points each month.</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4 p-4 bg-green-50 rounded-lg">
                      <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">2</div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Points = Dollars</h4>
                        <p className="text-sm text-gray-600">Each point equals $1 in guaranteed minimum scholarship at participating schools.</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4 p-4 bg-purple-50 rounded-lg">
                      <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-blue-900 font-bold">3</div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Redeem at 400+ Schools</h4>
                        <p className="text-sm text-gray-600">Use your points at any of our 400+ participating colleges and universities.</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-4 p-4 bg-yellow-50 rounded-lg">
                      <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600 font-bold">4</div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Coaches See Your Credits</h4>
                        <p className="text-sm text-gray-600">Coaches at participating schools can see your tuition rewards when viewing your profile.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Points History</h4>
                  {tuitionRewards?.points_history?.length > 0 ? (
                    <div className="space-y-2">
                      {tuitionRewards.points_history.slice(-5).reverse().map((entry, idx) => (
                        <div key={idx} className="flex justify-between p-2 border-b">
                          <div>
                            <div className="text-sm font-medium">{entry.reason}</div>
                            <div className="text-xs text-gray-500">{new Date(entry.date).toLocaleDateString()}</div>
                          </div>
                          <div className="text-green-600 font-bold">+{entry.points}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No points earned yet. Points accumulate monthly based on your membership.</p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="recruiting">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">College Interest & Recruiting</h3>
              <p className="text-gray-500 mb-6">Schools that have shown interest in your profile</p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <GraduationCap size={24} className="text-orange-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">University of Texas - Austin</div>
                      <div className="text-sm text-gray-500">Division I • SEC</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">High Interest</span>
                    <span className="text-xs text-gray-500">Last contact: Jan 20, 2024</span>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <GraduationCap size={24} className="text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Texas A&M University</div>
                      <div className="text-sm text-gray-500">Division I • SEC</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">Moderate Interest</span>
                    <span className="text-xs text-gray-500">Last contact: Dec 15, 2023</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 text-center">
                <Button className="bg-blue-900 hover:bg-purple-700 text-white">
                  View All Recruiting Activity
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="development">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Development Plan</h3>
              <p className="text-gray-500 mb-6">Your personalized development roadmap</p>
              
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-medium text-gray-900">Improve 60-Yard Dash Time</div>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">In Progress</span>
                  </div>
                  <div className="text-sm text-gray-500 mb-3">Target: Sub 6.5 seconds</div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '65%' }}></div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-medium text-gray-900">Increase Exit Velocity</div>
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">On Track</span>
                  </div>
                  <div className="text-sm text-gray-500 mb-3">Target: 95+ mph</div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '80%' }}></div>
                  </div>
                </div>

              </div>
            </div>
          </TabsContent>

          <TabsContent value="shares">
            <ShareActivityPanel
              athleteId={athlete?.athlete_id || playerProfile?.athlete_id}
              onShareClick={() => setShowShareCoach(true)}
            />
          </TabsContent>
        </Tabs>
        </div>
      </div>

      {/* Photo Crop Modal */}
      {cropModal && (
        <PhotoCropModal
          imageSrc={cropModal.src}
          aspectRatio={cropModal.type === "profile" ? 1 : 16 / 9}
          shape={cropModal.type === "profile" ? "round" : "rect"}
          onSave={handleCroppedUpload}
          onClose={() => setCropModal(null)}
          saving={!!uploadingPhoto}
        />
      )}

      {/* Profile Edit Modal */}
      {showEditProfile && (
        <ProfileEditModal
          profile={playerProfile || athlete}
          onClose={() => setShowEditProfile(false)}
          onSave={handleProfileSave}
        />
      )}

      {/* Share with Coach Modal — generates token-gated private link */}
      <ShareWithCoachModal
        open={showShareCoach}
        onClose={() => setShowShareCoach(false)}
        athleteId={athlete?.athlete_id || playerProfile?.athlete_id}
        athleteName={displayName}
      />
    </div>
  );
}
